import { AskRequestPriorities } from '@hone-automation/common';
import { FilterView } from '../../../../Queue/QueueContext';
import { DropOperation } from './dropRules';
import { BatchOperationType, BatchOperationValue } from '../../../contexts';

/**
 * Mutation payload for a single field update
 */
export interface DropMutationPayload {
  id: string;
  status?: 'Open' | 'In Progress' | 'Closed';
  priority?: typeof AskRequestPriorities.Blocker | typeof AskRequestPriorities.Normal;
  assignee?: string | null;
  parentId?: string | null;
}

/**
 * Operation to execute - used by batch system
 */
export interface DropOperationItem {
  type: BatchOperationType;
  value: BatchOperationValue;
}

/**
 * Get the list of operations to execute for a drop operation.
 * This is the single source of truth for how drop operations are split into mutations.
 *
 * IMPORTANT: Backend doesn't allow updating status/priority AND assignee in one call.
 * This function splits the operation into separate mutation items.
 *
 * @param dropOperation - The drop operation from getDropOperation()
 * @param meId - Current user ID (for resolving 'me' assignee)
 * @returns Array of operations to execute in order
 */
export const getDropOperationItems = (dropOperation: DropOperation, meId: string | undefined): DropOperationItem[] => {
  const items: DropOperationItem[] = [];

  // Order matters - assignee should be set before status changes
  if (dropOperation.setAssignee !== undefined) {
    const assigneeValue = dropOperation.setAssignee === 'me' ? meId ?? null : dropOperation.setAssignee;
    items.push({ type: 'setAssignee', value: assigneeValue });
  }

  if (dropOperation.setStatus) {
    items.push({ type: 'setStatus', value: dropOperation.setStatus });
  }

  if (dropOperation.setPriority) {
    items.push({ type: 'setPriority', value: dropOperation.setPriority });
  }

  if (dropOperation.setParentId !== undefined) {
    items.push({ type: 'setParentId', value: dropOperation.setParentId });
  }

  return items;
};

/**
 * Build a mutation payload for a single operation.
 *
 * @param requestId - The request ID to update
 * @param operationType - The type of operation
 * @param value - The value to set
 * @returns Mutation payload object
 */
export const buildMutationPayload = (
  requestId: string,
  operationType: BatchOperationType,
  value: BatchOperationValue
): DropMutationPayload => {
  const payload: DropMutationPayload = { id: requestId };

  if (operationType === 'setStatus') {
    payload.status = value as 'Open' | 'In Progress' | 'Closed';
  } else if (operationType === 'setPriority') {
    payload.priority = value === 'Blocker' ? AskRequestPriorities.Blocker : AskRequestPriorities.Normal;
  } else if (operationType === 'setAssignee') {
    payload.assignee = value as string | null;
  } else if (operationType === 'setParentId') {
    payload.parentId = value as string | null;
  }

  return payload;
};

/**
 * Execute a drop operation using direct mutations (fire-and-forget).
 * Use this for mobile or when batch progress tracking isn't needed.
 *
 * @param requestIds - Array of request IDs to update
 * @param dropOperation - The drop operation from getDropOperation()
 * @param meId - Current user ID (for resolving 'me' assignee)
 * @param mutate - Mutation function to call
 */
export const executeDropOperationDirect = (
  requestIds: (string | number)[],
  dropOperation: DropOperation,
  meId: string | undefined,
  mutate: (payload: DropMutationPayload) => void
): void => {
  const operationItems = getDropOperationItems(dropOperation, meId);

  requestIds.forEach(requestId => {
    operationItems.forEach(item => {
      const payload = buildMutationPayload(String(requestId), item.type, item.value);
      mutate(payload);
    });
  });
};

/**
 * Execute a drop operation using the batch queue system.
 * Use this for desktop where progress tracking is needed.
 *
 * @param requestIds - Array of request IDs to update
 * @param dropOperation - The drop operation from getDropOperation()
 * @param meId - Current user ID (for resolving 'me' assignee)
 * @param targetFilter - Target filter for batch progress tracking
 * @param addBatch - Batch queue addBatch function
 * @param onProcess - Callback to process each item
 */
export const executeDropOperationBatched = (
  requestIds: string[],
  dropOperation: DropOperation,
  meId: string | undefined,
  targetFilter: FilterView,
  addBatch: (
    requestIds: string[],
    operation: BatchOperationType,
    value: BatchOperationValue,
    targetFilter: FilterView,
    onProcess: (requestId: string, operation: BatchOperationType, value: BatchOperationValue) => Promise<void>
  ) => void,
  onProcess: (requestId: string, operation: BatchOperationType, value: BatchOperationValue) => Promise<void>
): void => {
  const operationItems = getDropOperationItems(dropOperation, meId);

  operationItems.forEach(item => {
    addBatch(requestIds, item.type, item.value, targetFilter, onProcess);
  });
};
